import { createFileRoute, lazyRouteComponent, redirect } from "@tanstack/react-router"
import { z } from "zod/v4"

import { parsePeriod } from "@atlant/agent-contract"

/**
 * Filters a link can open the page with, such as one the assistant offers.
 *
 * The page keeps its filters in a store rather than the URL, so these are copied into the
 * store on arrival and then dropped from the address: left there, they would disagree with
 * the toolbar as soon as the reader changed anything, and would apply again on reload.
 */
const searchSchema = z.object({
  aircraftId: z.string().optional().catch(undefined),
  /** YYYY-MM. */
  period: z
    .string()
    .refine((value) => parsePeriod(value) !== undefined)
    .optional()
    .catch(undefined),
})

export const Route = createFileRoute("/_authenticated/$slug/flights")({
  staticData: { title: "Flights" },
  validateSearch: searchSchema,
  beforeLoad: async ({ search: { aircraftId, period }, params, preload }) => {
    // A preload on hover is not an arrival: seeding then would change the filters of a
    // page the reader may never open.
    if (preload || (!aircraftId && !period)) return

    // Imported here rather than at the top, so the store stays in the page's lazy chunk
    // instead of loading with the route tree for every reader.
    const { useFlightsFilterStore } =
      await import("#pages/authenticated/flights/components/toolbar/flights-filter-store.ts")
    const store = useFlightsFilterStore.getState()

    // A link stands for the whole page, as its label does: "Flights · Sep 2026" means
    // every aircraft that month, so whatever the link leaves out goes back to the page's
    // default rather than keeping a search or an aircraft from the reader's last visit.
    // The view (table, timeline) is kept: it is how the reader likes to look, not what.
    store.setAircraftId(aircraftId ?? "")
    const month = period ? parsePeriod(period) : undefined
    // The store counts months from 0, as `Date` does.
    if (month) store.setMonth(month.month - 1, month.year)
    else store.goToToday()
    store.setSearch("")
    store.resetFilters()

    throw redirect({ to: "/$slug/flights", params, search: {}, replace: true })
  },
  component: lazyRouteComponent(
    () => import("#pages/authenticated/flights/page.tsx"),
    "Flights",
  ),
})
